import { AllocationStatus, isDefined } from '@zorro/clients';
import { AlertVariant } from '@zorro/design-system/v2/Alert';
import { useMonolithQuery } from '@zorro/web/ui-utils';
import { $enum } from 'ts-enum-util';

export type InitialAllocationGateResult = {
  isSubmitLocked: boolean;
  alert?: {
    variant: AlertVariant;
    message: string;
  };
};

const FUNDS_PENDING_SETTLEMENT: InitialAllocationGateResult = {
  isSubmitLocked: true,
  alert: {
    variant: 'warning',
    message:
      'Funds are on the way but have not settled yet. You cannot continue until the account is funded.',
  },
};

const FUNDING_FAILED: InitialAllocationGateResult = {
  isSubmitLocked: true,
  alert: {
    variant: 'error',
    message:
      'Funding could not be completed. Needs a CSM to resolve the employer funding. No automatic retry.',
  },
};

const UNEXPECTED_STATUS: InitialAllocationGateResult = {
  isSubmitLocked: true,
  alert: {
    variant: 'error',
    message:
      'Zorro Pay funding is in an unexpected status. Please contact support.',
  },
};

const getSuccessMessage = (
  employeeName: string
): InitialAllocationGateResult => ({
  isSubmitLocked: false,
  alert: {
    variant: 'success',
    message: `${employeeName}'s Zorro Pay account is funded.`,
  },
});

export const mapAllocationStatusToMessage = (
  status: AllocationStatus | undefined,
  employeeName: string
): InitialAllocationGateResult => {
  if (!isDefined(status)) {
    return UNEXPECTED_STATUS;
  }

  return $enum.mapValue(status).with<InitialAllocationGateResult>({
    [AllocationStatus.SCHEDULED]: UNEXPECTED_STATUS,
    [AllocationStatus.AWAITING_DEPENDENCY]: FUNDS_PENDING_SETTLEMENT,
    [AllocationStatus.PROCESSING]: {
      isSubmitLocked: false,
    },
    [AllocationStatus.SUCCEEDED]: getSuccessMessage(employeeName),
    [AllocationStatus.FAILED]: FUNDING_FAILED,
    [AllocationStatus.CANCELED]: UNEXPECTED_STATUS,
  });
};

const ALLOCATION_STATUS_POLL_INTERVAL_MS = 20_000;

type Props = {
  employeeId: string;
  benefitId?: string;
  employeeName: string;
  enabled: boolean;
};

export function useInitialAllocationGate({
  employeeId,
  benefitId,
  employeeName,
  enabled,
}: Props): InitialAllocationGateResult {
  const isGateActive = enabled && isDefined(benefitId);

  const { data, isLoading, isError } = useMonolithQuery({
    method: 'paymentsControllerGetInitialAllocationStatus',
    params: isDefined(benefitId) && [employeeId, benefitId],
    enabled: isGateActive,
    refetchInterval: isGateActive && ALLOCATION_STATUS_POLL_INTERVAL_MS,
  });

  if (!isGateActive) {
    return { isSubmitLocked: false };
  }

  if (isLoading) {
    return { isSubmitLocked: true };
  }

  if (isError) {
    return mapAllocationStatusToMessage(undefined, employeeName);
  }

  return mapAllocationStatusToMessage(data?.status, employeeName);
}
